// Route access policy per role.
// Applies AFTER authentication. Prevents direct URL bar navigation
// to admin routes for read-only / restricted roles.
//
// - super_admin / superadmin / admin / kepala_cabang / teknisi / customer service:
//     no policy = full access (menu already filters what they see).
// - owner (read-only): explicit DENY list of sensitive config/system routes.
// - voucher_agent (very restricted): explicit ALLOW list of only a few routes.
//
// Route paths are relative to `/app/` (no leading slash). Nested paths supported
// via prefix match (e.g. entry `customers/bulk-import` matches `/app/customers/bulk-import`).
//
// Related bugfix: URL-bar route leak — owner/voucher_agent could ketik URL langsung
// bypass sidebar hiding and reach admin pages.

const OWNER_DENIED_ROUTES = [
  // Network / MikroTik config
  'mikrotik', 'mikrotik-monitoring', 'ip-pools',
  'l2tp-management', 'tenant-vpn',
  'radius-management', 'radius-config',
  'radius-guide', 'radius-only-guide', 'hybrid-mode-guide', 'vpn-setup-guide',
  'gateway-permissions', 'freeradius', 'acs-server', 'acs-config',
  'ont-provisioning', 'wan-devices',
  // User / Role management
  'users', 'roles', 'role-matrix',
  // Admin / System tools
  'admin-tools', 'system-tools', 'superadmin-tools',
  'super-admin', 'superadmin-home',
  'superadmin-vpn-management', 'superadmin-router-status',
  'backup-manager', 'migrasi-data', 'trash',
  'system-settings', 'system-info', 'system-monitor', 'app-version',
  'audit-logs', 'email-logs', 'reconciliation',
  // Company / Tenant config
  'company-settings', 'company-profile', 'company-info',
  'tenant-billing', 'tenant-subscription', 'subscription-plans',
  'branch-management', 'mpanel',
  // Isolir / Automation config
  'isolir-page-editor', 'isolir-global-editor', 'isolir-management',
  'automation', 'reminder-settings',
  // Communication config
  'whatsapp', 'email-smtp', 'whatsapp-cost',
  // Voucher config (bukan management)
  'voucher-migration', 'voucher-template-editor', 'hotspot-customizer',
  'voucher-agents',
  // Bulk / Bulk import
  'customers/bulk-import',
  // Payment gateway config
  'payment-gateway',
  // Settings umum
  'settings',
  // Owner-of-owners page (bukan self)
  'owners',
];

const TEKNISI_DENIED_ROUTES = [
  'role-matrix',
  'help',
];

const VOUCHER_AGENT_ALLOWED_ROUTES = [
  '',                    // /app root
  'dashboard',
  'manajemen-voucher',
  'agent-dashboard',
  'profile',
  '403',
];

export const ROLE_ROUTE_POLICY = {
  owner: { strategy: 'deny', routes: OWNER_DENIED_ROUTES },
  teknisi: { strategy: 'deny', routes: TEKNISI_DENIED_ROUTES },
  voucher_agent: { strategy: 'allow', routes: VOUCHER_AGENT_ALLOWED_ROUTES },
};

/**
 * Match segment against a policy route (supports prefix + nested).
 * "mikrotik" matches /app/mikrotik and /app/mikrotik/anything
 * "customers/bulk-import" matches /app/customers/bulk-import[/...]
 */
function segMatches(routeEntry, strippedPath) {
  if (!routeEntry) return strippedPath === '';
  return strippedPath === routeEntry || strippedPath.startsWith(routeEntry + '/');
}

/**
 * Determine if user's role may access the given pathname.
 * @param {string} role - user.role (case-insensitive)
 * @param {string} pathname - full pathname e.g. `/app/mikrotik`
 * @returns {boolean}
 */
export function isRouteAllowedForRole(role, pathname) {
  const normalizedRole = (role || '').toLowerCase();
  if (['super_admin', 'superadmin'].includes(normalizedRole)) return true;

  const policy = ROLE_ROUTE_POLICY[normalizedRole];
  if (!policy) return true; // roles without explicit policy = full access

  // Strip /app/ prefix and query/hash
  const stripped = (pathname || '')
    .replace(/^\/+app\/?/, '')
    .split('?')[0]
    .split('#')[0]
    .replace(/\/+$/, '');

  if (policy.strategy === 'deny') {
    // Denied if ANY denied route matches
    return !policy.routes.some(r => segMatches(r, stripped));
  }
  if (policy.strategy === 'allow') {
    // Allowed if ANY allowed route matches
    return policy.routes.some(r => segMatches(r, stripped));
  }
  return true;
}

/**
 * Preferred landing route for a role (used when 403 shown or root visited).
 */
export function getLandingRouteForRole(role) {
  const normalizedRole = (role || '').toLowerCase();
  if (normalizedRole === 'voucher_agent') return '/app/manajemen-voucher';
  return '/app/dashboard';
}
